import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Header, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import OTPInput from "../components/OTPInput";
import { KeyRound, ArrowRight } from "lucide-react";

export default function AuthPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [busy, setBusy] = useState(false);

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  const onLogin = async (e) => {
    e?.preventDefault?.();
    if (!email || code.length !== 4) {
      toast.error("Enter email and your 4-digit code");
      return;
    }
    setBusy(true);
    try {
      await login(email, code);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F2]">
      <Header />
      <main className="flex-1 py-12 sm:py-20">
        <div className="max-w-md mx-auto px-4">
          <div className="mb-8">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#0F6B3B]" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#0F6B3B]">
                Coaching clients
              </span>
            </div>
            <h1 className="mt-5 font-display text-[2rem] sm:text-4xl font-bold leading-[1.05] tracking-[-0.03em] text-[#14211B]">
              Enter your code
            </h1>
            <p className="mt-3 text-[15px] leading-relaxed text-[#56655C]">
              Fresh 4-digit codes are issued every Sunday at 6am.
            </p>
          </div>

          <div className="border border-[#E7E3D7] bg-white p-5 sm:p-7">
            <form onSubmit={onLogin} className="space-y-6">
              <div>
                <Label
                  htmlFor="login-email"
                  className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6A776F]"
                >
                  Email
                </Label>
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  className="mt-2 h-12 border-[#DDE3D5] bg-white text-base text-[#14211B]"
                />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6A776F]">
                  4-digit code
                </Label>
                <div className="mt-4 flex justify-center">
                  <OTPInput value={code} onChange={setCode} />
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="h-12 w-full rounded-md bg-[#0F6B3B] text-sm font-semibold text-white hover:bg-[#0B5730]"
                data-testid="login-submit-btn"
              >
                {busy ? "Checking…" : "Enter dashboard"}
                {!busy && <ArrowRight className="ml-2.5 h-4 w-4" />}
              </Button>
            </form>
          </div>

          <div className="mt-7 flex items-start gap-3 text-sm text-[#56655C]">
            <KeyRound className="mt-0.5 h-4 w-4 flex-none text-[#0F6B3B]" />
            <div>
              Not coaching with Abraham?{" "}
              <Link
                to="/subscribe"
                className="font-semibold text-[#0F6B3B] hover:underline"
                data-testid="to-subscribe-link"
              >
                Subscribe for €14.99 / month
              </Link>
            </div>
          </div>
          <div className="mt-4 text-xs text-[#8A968E]">
            <Link to="/" className="hover:text-[#0F6B3B]" data-testid="back-home-link">
              ← Back to home
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
